import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Save, X, GraduationCap, BookOpen, 
  ChevronDown, ChevronUp, Globe, Euro, Clock, 
  Check, AlertCircle, Loader2, FileText, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import AdminPinGuard from '../components/AdminPinGuard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const CATEGORIES = [
  { value: 'lashes', label: 'Cils & Sourcils' },
  { value: 'massage', label: 'Massage' },
  { value: 'afro_hair', label: 'Coiffure Afro' },
  { value: 'facial', label: 'Soins du Visage' },
  { value: 'body', label: 'Soins du Corps' },
  { value: 'makeup', label: 'Maquillage' },
];

const AdminCoursesPage = () => {
  const { language } = useLanguage();
  const [adminPin, setAdminPin] = useState('');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/cms/courses`, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminPin) {
      fetchCourses();
    }
  }, [adminPin]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleCreateCourse = async (data) => {
    try {
      await axios.post(`${API}/cms/courses`, data, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      showMessage('success', 'Formation créée avec succès');
      fetchCourses();
      setEditingCourse(null);
    } catch (error) {
      showMessage('error', 'Erreur lors de la création');
    }
  };

  const handleUpdateCourse = async (courseId, data) => {
    try {
      await axios.put(`${API}/cms/courses/${courseId}`, data, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      showMessage('success', 'Formation mise à jour');
      fetchCourses();
      setEditingCourse(null);
    } catch (error) {
      showMessage('error', 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette formation ?')) return;
    try {
      await axios.delete(`${API}/cms/courses/${courseId}`, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      showMessage('success', 'Formation supprimée');
      fetchCourses();
    } catch (error) {
      showMessage('error', 'Erreur lors de la suppression');
    }
  };

  const handleAddModule = async (courseId, data) => {
    try {
      await axios.post(`${API}/cms/courses/${courseId}/modules`, data, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      showMessage('success', 'Module ajouté');
      fetchCourses();
      setEditingModule(null);
    } catch (error) {
      showMessage('error', 'Erreur lors de l\'ajout du module');
    }
  };

  const handleAddLesson = async (courseId, moduleId, data) => {
    try {
      await axios.post(`${API}/cms/courses/${courseId}/modules/${moduleId}/lessons`, data, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      showMessage('success', 'Leçon ajoutée');
      fetchCourses();
      setEditingLesson(null);
    } catch (error) {
      showMessage('error', 'Erreur lors de l\'ajout de la leçon');
    }
  };

  const handleSetModuleQuiz = async (courseId, moduleId, data) => {
    try {
      await axios.post(`${API}/cms/courses/${courseId}/modules/${moduleId}/quiz`, data, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      showMessage('success', 'Quiz configuré');
      fetchCourses();
      setEditingQuiz(null);
    } catch (error) {
      showMessage('error', 'Erreur lors de la configuration du quiz');
    }
  };

  const handleSetFinalExam = async (courseId, data) => {
    try {
      await axios.post(`${API}/cms/courses/${courseId}/final-exam`, data, {
        headers: { 'X-Admin-Pin': adminPin }
      });
      showMessage('success', 'Examen final configuré');
      fetchCourses();
      setEditingQuiz(null);
    } catch (error) {
      showMessage('error', 'Erreur lors de la configuration de l\'examen');
    }
  };

  if (!adminPin) {
    return <AdminPinGuard onSuccess={setAdminPin} />;
  }

  return (
    <div className="min-h-screen bg-kamit-cream pt-24 pb-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-kamit-brown flex items-center gap-3">
              <GraduationCap className="h-8 w-8 text-kamit-gold" />
              Gestion des Formations
            </h1>
            <p className="text-kamit-brown/60 mt-1">
              Créez et gérez vos formations en ligne
            </p>
          </div>
          <Button
            onClick={() => setEditingCourse({ isNew: true })}
            className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Formation
          </Button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-12 w-12 text-kamit-gold animate-spin" />
          </div>
        ) : (
          <>
            {/* Course Editor Modal */}
            {editingCourse && (
              <CourseEditor
                course={editingCourse.isNew ? null : editingCourse}
                onSave={(data) => editingCourse.isNew ? handleCreateCourse(data) : handleUpdateCourse(editingCourse.id, data)}
                onCancel={() => setEditingCourse(null)}
              />
            )}

            {/* Module Editor Modal */}
            {editingModule && (
              <ModuleEditor
                onSave={(data) => handleAddModule(editingModule.courseId, data)}
                onCancel={() => setEditingModule(null)}
              />
            )}

            {/* Lesson Editor Modal */}
            {editingLesson && (
              <LessonEditor
                onSave={(data) => handleAddLesson(editingLesson.courseId, editingLesson.moduleId, data)}
                onCancel={() => setEditingLesson(null)}
              />
            )}

            {/* Quiz Editor Modal */}
            {editingQuiz && (
              <QuizEditor
                isFinalExam={editingQuiz.isFinalExam}
                onSave={(data) => editingQuiz.isFinalExam 
                  ? handleSetFinalExam(editingQuiz.courseId, data)
                  : handleSetModuleQuiz(editingQuiz.courseId, editingQuiz.moduleId, data)
                }
                onCancel={() => setEditingQuiz(null)}
              />
            )}

            {/* Courses List */}
            <div className="space-y-4">
              {courses.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <GraduationCap className="h-16 w-16 text-kamit-gold/30 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-kamit-brown mb-2">Aucune formation</h3>
                  <p className="text-kamit-brown/60 mb-6">Créez votre première formation</p>
                  <Button
                    onClick={() => setEditingCourse({ isNew: true })}
                    className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer une Formation
                  </Button>
                </div>
              ) : (
                courses.map((course) => (
                  <div key={course.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                    {/* Course Header */}
                    <div 
                      className="p-6 cursor-pointer hover:bg-kamit-cream/50 transition-colors"
                      onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-kamit-gold/20 rounded-full flex items-center justify-center">
                            <GraduationCap className="h-6 w-6 text-kamit-gold" />
                          </div>
                          <div>
                            <h3 className="font-heading text-lg text-kamit-brown">
                              {course.title_fr || course.title_en}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-kamit-brown/60">
                              <span className="flex items-center gap-1">
                                <Euro className="h-4 w-4" />
                                {course.price}€
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {course.total_hours}h
                              </span>
                              <span className="flex items-center gap-1">
                                <BookOpen className="h-4 w-4" />
                                {course.modules?.length || 0} modules
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setEditingCourse(course); }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDeleteCourse(course.id); }}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {expandedCourse === course.id ? (
                            <ChevronUp className="h-5 w-5 text-kamit-brown/50" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-kamit-brown/50" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedCourse === course.id && (
                      <div className="border-t border-kamit-cream p-6">
                        {/* Modules */}
                        <div className="mb-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-kamit-brown flex items-center gap-2">
                              <BookOpen className="h-5 w-5 text-kamit-gold" />
                              Modules
                            </h4>
                            <Button
                              size="sm"
                              onClick={() => setEditingModule({ courseId: course.id })}
                              className="bg-kamit-brown hover:bg-kamit-dark text-kamit-beige"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Ajouter Module
                            </Button>
                          </div>
                          {course.modules?.length > 0 ? (
                            <div className="space-y-3">
                              {course.modules.map((module, idx) => (
                                <div key={module.id} className="bg-kamit-cream rounded-lg p-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <span className="text-kamit-gold font-medium mr-2">Module {idx + 1}:</span>
                                      <span className="text-kamit-brown">{module.title_fr || module.title_en}</span>
                                      <span className="text-kamit-brown/60 text-sm ml-2">
                                        ({module.lessons?.length || 0} leçons, {module.required_time_minutes}min requis)
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditingLesson({ courseId: course.id, moduleId: module.id })}
                                      >
                                        <FileText className="h-4 w-4 mr-1" />
                                        Leçon
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditingQuiz({ courseId: course.id, moduleId: module.id })}
                                      >
                                        <HelpCircle className="h-4 w-4 mr-1" />
                                        Quiz
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-kamit-brown/50 text-center py-4">Aucun module</p>
                          )}
                        </div>

                        {/* Final Exam */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-kamit-brown flex items-center gap-2">
                              <HelpCircle className="h-5 w-5 text-kamit-gold" />
                              Examen Final (30 questions, 100% requis)
                            </h4>
                            <Button
                              size="sm"
                              onClick={() => setEditingQuiz({ courseId: course.id, isFinalExam: true })}
                              className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark"
                            >
                              {course.final_exam ? <Edit2 className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                              {course.final_exam ? 'Modifier' : 'Créer'} Examen
                            </Button>
                          </div>
                          {course.final_exam ? (
                            <div className="bg-kamit-gold/10 rounded-lg p-4 text-center">
                              <Check className="h-6 w-6 text-green-600 mx-auto mb-2" />
                              <p className="text-kamit-brown">
                                Examen configuré: {course.final_exam.questions?.length || 0} questions
                              </p>
                            </div>
                          ) : (
                            <p className="text-kamit-brown/50 text-center py-4">Examen non configuré</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Course Editor Component
const CourseEditor = ({ course, onSave, onCancel }) => {
  const [data, setData] = useState({
    title_en: course?.title_en || '',
    title_fr: course?.title_fr || '',
    title_pt: course?.title_pt || '',
    title_de: course?.title_de || '',
    title_es: course?.title_es || '',
    description_en: course?.description_en || '',
    description_fr: course?.description_fr || '',
    description_pt: course?.description_pt || '',
    description_de: course?.description_de || '',
    description_es: course?.description_es || '',
    category: course?.category || 'lashes',
    price: course?.price || 350,
    total_hours: course?.total_hours || 30,
    image_url: course?.image_url || '',
    certificate_title_en: course?.certificate_title_en || '',
    certificate_title_fr: course?.certificate_title_fr || '',
  });

  const [activeLang, setActiveLang] = useState('fr');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-kamit-cream">
          <h2 className="font-heading text-xl text-kamit-brown">
            {course ? 'Modifier la Formation' : 'Nouvelle Formation'}
          </h2>
        </div>
        <div className="p-6 space-y-4">
          {/* Language Tabs */}
          <div className="flex gap-2 mb-4">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => setActiveLang(lang.code)}
                className={`px-3 py-1 rounded text-sm ${
                  activeLang === lang.code ? 'bg-kamit-gold text-kamit-dark' : 'bg-kamit-cream text-kamit-brown'
                }`}
              >
                {lang.flag} {lang.code.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Title */}
          <div>
            <Label>Titre ({activeLang.toUpperCase()})</Label>
            <Input
              value={data[`title_${activeLang}`]}
              onChange={(e) => setData({...data, [`title_${activeLang}`]: e.target.value})}
              placeholder="Ex: Expert Extensions de Cils"
            />
          </div>

          {/* Description */}
          <div>
            <Label>Description ({activeLang.toUpperCase()})</Label>
            <Textarea
              value={data[`description_${activeLang}`]}
              onChange={(e) => setData({...data, [`description_${activeLang}`]: e.target.value})}
              placeholder="Description de la formation..."
              rows={3}
            />
          </div>

          {/* Category & Price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Catégorie</Label>
              <select
                value={data.category}
                onChange={(e) => setData({...data, category: e.target.value})}
                className="w-full border rounded-md px-3 py-2"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Prix (€)</Label>
              <Input
                type="number"
                value={data.price}
                onChange={(e) => setData({...data, price: Number(e.target.value)})}
              />
            </div>
          </div>

          {/* Hours & Image */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Heures totales</Label>
              <Input
                type="number"
                value={data.total_hours}
                onChange={(e) => setData({...data, total_hours: Number(e.target.value)})}
              />
            </div>
            <div>
              <Label>URL Image</Label>
              <Input
                value={data.image_url}
                onChange={(e) => setData({...data, image_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Certificate Title */}
          <div>
            <Label>Titre du Certificat ({activeLang.toUpperCase()})</Label>
            <Input
              value={data[`certificate_title_${activeLang}`] || ''}
              onChange={(e) => setData({...data, [`certificate_title_${activeLang}`]: e.target.value})}
              placeholder="Ex: Certificat Expert Extensions de Cils"
            />
          </div>
        </div>
        <div className="p-6 border-t border-kamit-cream flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button onClick={() => onSave(data)} className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark">
            <Save className="h-4 w-4 mr-2" />
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

// Module Editor Component
const ModuleEditor = ({ onSave, onCancel }) => {
  const [data, setData] = useState({
    title_en: '',
    title_fr: '',
    description_en: '',
    description_fr: '',
    required_time_minutes: 30,
    order: 0
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="p-6 border-b border-kamit-cream">
          <h2 className="font-heading text-xl text-kamit-brown">Nouveau Module</h2>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label>Titre (FR)</Label>
            <Input
              value={data.title_fr}
              onChange={(e) => setData({...data, title_fr: e.target.value})}
              placeholder="Ex: Introduction aux Extensions"
            />
          </div>
          <div>
            <Label>Titre (EN)</Label>
            <Input
              value={data.title_en}
              onChange={(e) => setData({...data, title_en: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Temps requis (minutes)</Label>
              <Input
                type="number"
                value={data.required_time_minutes}
                onChange={(e) => setData({...data, required_time_minutes: Number(e.target.value)})}
              />
            </div>
            <div>
              <Label>Ordre</Label>
              <Input
                type="number"
                value={data.order}
                onChange={(e) => setData({...data, order: Number(e.target.value)})}
              />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-kamit-cream flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button onClick={() => onSave(data)} className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark">
            Ajouter
          </Button>
        </div>
      </div>
    </div>
  );
};

// Lesson Editor Component
const LessonEditor = ({ onSave, onCancel }) => {
  const [data, setData] = useState({
    title_en: '',
    title_fr: '',
    content_en: '',
    content_fr: '',
    content_type: 'text',
    video_url: '',
    duration_minutes: 10,
    order: 0
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-kamit-cream">
          <h2 className="font-heading text-xl text-kamit-brown">Nouvelle Leçon</h2>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label>Titre (FR)</Label>
            <Input
              value={data.title_fr}
              onChange={(e) => setData({...data, title_fr: e.target.value})}
            />
          </div>
          <div>
            <Label>Type de contenu</Label>
            <select
              value={data.content_type}
              onChange={(e) => setData({...data, content_type: e.target.value})}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="text">Texte</option>
              <option value="video">Vidéo</option>
              <option value="mixed">Mixte</option>
            </select>
          </div>
          {data.content_type !== 'text' && (
            <div>
              <Label>URL Vidéo</Label>
              <Input
                value={data.video_url}
                onChange={(e) => setData({...data, video_url: e.target.value})}
                placeholder="https://youtube.com/..."
              />
            </div>
          )}
          <div>
            <Label>Contenu (FR)</Label>
            <Textarea
              value={data.content_fr}
              onChange={(e) => setData({...data, content_fr: e.target.value})}
              rows={6}
              placeholder="Contenu de la leçon..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Durée (minutes)</Label>
              <Input
                type="number"
                value={data.duration_minutes}
                onChange={(e) => setData({...data, duration_minutes: Number(e.target.value)})}
              />
            </div>
            <div>
              <Label>Ordre</Label>
              <Input
                type="number"
                value={data.order}
                onChange={(e) => setData({...data, order: Number(e.target.value)})}
              />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-kamit-cream flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button onClick={() => onSave(data)} className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark">
            Ajouter
          </Button>
        </div>
      </div>
    </div>
  );
};

// Quiz Editor Component
const QuizEditor = ({ isFinalExam, onSave, onCancel }) => {
  const [data, setData] = useState({
    title_fr: isFinalExam ? 'Examen Final' : 'Quiz du Module',
    title_en: isFinalExam ? 'Final Exam' : 'Module Quiz',
    questions: [],
    passing_score: isFinalExam ? 100 : 90,
    is_final_exam: isFinalExam
  });

  const [newQuestion, setNewQuestion] = useState({
    question_fr: '',
    question_en: '',
    options: [
      { fr: '', en: '' },
      { fr: '', en: '' },
      { fr: '', en: '' },
      { fr: '', en: '' }
    ],
    correct_answer: 0
  });

  const addQuestion = () => {
    if (!newQuestion.question_fr) return;
    setData({
      ...data,
      questions: [...data.questions, { ...newQuestion, id: `q_${Date.now()}` }]
    });
    setNewQuestion({
      question_fr: '',
      question_en: '',
      options: [{ fr: '', en: '' }, { fr: '', en: '' }, { fr: '', en: '' }, { fr: '', en: '' }],
      correct_answer: 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-kamit-cream">
          <h2 className="font-heading text-xl text-kamit-brown">
            {isFinalExam ? 'Examen Final (30 questions requises)' : 'Quiz du Module'}
          </h2>
        </div>
        <div className="p-6 space-y-6">
          {/* Existing Questions */}
          {data.questions.length > 0 && (
            <div className="space-y-2">
              <Label>Questions ajoutées ({data.questions.length})</Label>
              {data.questions.map((q, idx) => (
                <div key={q.id} className="bg-kamit-cream rounded p-3 flex justify-between items-center">
                  <span>Q{idx + 1}: {q.question_fr}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setData({...data, questions: data.questions.filter((_, i) => i !== idx)})}
                    className="text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add New Question */}
          <div className="border-2 border-dashed border-kamit-gold/30 rounded-xl p-4 space-y-4">
            <Label className="text-kamit-gold">Ajouter une question</Label>
            <div>
              <Label>Question (FR)</Label>
              <Input
                value={newQuestion.question_fr}
                onChange={(e) => setNewQuestion({...newQuestion, question_fr: e.target.value})}
                placeholder="Posez votre question..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {newQuestion.options.map((opt, idx) => (
                <div key={idx}>
                  <Label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="correct"
                      checked={newQuestion.correct_answer === idx}
                      onChange={() => setNewQuestion({...newQuestion, correct_answer: idx})}
                    />
                    Option {idx + 1} {newQuestion.correct_answer === idx && '(Correcte)'}
                  </Label>
                  <Input
                    value={opt.fr}
                    onChange={(e) => {
                      const newOptions = [...newQuestion.options];
                      newOptions[idx] = { ...newOptions[idx], fr: e.target.value };
                      setNewQuestion({...newQuestion, options: newOptions});
                    }}
                    placeholder={`Réponse ${idx + 1}`}
                  />
                </div>
              ))}
            </div>
            <Button onClick={addQuestion} variant="outline" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter cette question
            </Button>
          </div>

          {/* Passing Score */}
          <div>
            <Label>Score de réussite (%)</Label>
            <Input
              type="number"
              value={data.passing_score}
              onChange={(e) => setData({...data, passing_score: Number(e.target.value)})}
              disabled={isFinalExam}
            />
            {isFinalExam && <p className="text-sm text-kamit-brown/60 mt-1">L'examen final requiert 100%</p>}
          </div>
        </div>
        <div className="p-6 border-t border-kamit-cream flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button 
            onClick={() => onSave(data)} 
            disabled={isFinalExam && data.questions.length < 30}
            className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark"
          >
            <Save className="h-4 w-4 mr-2" />
            Enregistrer ({data.questions.length} questions)
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminCoursesPage;
